import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { networkManager, SessionMessages } from '../network/NetworkManager';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/game.config';
import { PlayerStats, StatsTracker } from './StatsTracker';

export class GameStateManager {
    private scene: Phaser.Scene;
    private player: Player;
    private remotePlayers: Map<string, Player>;
    private statsTracker?: StatsTracker;
    public isGameOver: boolean = false;

    constructor(scene: Phaser.Scene, player: Player, remotePlayers: Map<string, Player>) {
        this.scene = scene;
        this.player = player;
        this.remotePlayers = remotePlayers;
    }

    public setStatsTracker(tracker: StatsTracker): void {
        this.statsTracker = tracker;
    }

    public handlePlayerDeath(): void {
        const x = this.player.x;
        const y = this.player.y;

        // Stop all movement
        this.player.setAcceleration(0, 0);
        this.player.setVelocity(0, 0);
        if (this.player.body) this.player.body.enable = false;

        // "Eliminated!" text
        const deathText = this.scene.add.text(x, y - 40, 'ELIMINATED!', {
            fontSize: '24px',
            fontStyle: 'bold',
            color: '#ff0000',
            stroke: '#000000',
            strokeThickness: 4
        }).setOrigin(0.5).setDepth(100);

        // Inflate and pop animation
        this.scene.tweens.add({
            targets: this.player,
            scale: 2,
            alpha: 0.5,
            duration: 300,
            ease: 'Back.easeIn',
            onComplete: () => {
                // Pop effect: camera shake
                this.scene.cameras.main.shake(500, 0.01);
                this.player.setVisible(false);

                // Create "particles" (simple circles)
                for (let i = 0; i < 15; i++) {
                    const p = this.scene.add.circle(x, y, 3, 0x00b894);
                    this.scene.physics.add.existing(p);
                    const body = p.body as Phaser.Physics.Arcade.Body;
                    const angle = Math.random() * Math.PI * 2;
                    const speed = 100 + Math.random() * 200;
                    body.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed);

                    this.scene.tweens.add({
                        targets: p,
                        alpha: 0,
                        scale: 0,
                        duration: 800,
                        onComplete: () => p.destroy()
                    });
                }

                this.scene.time.delayedCall(1000, () => {
                    this.checkWinCondition(true); // isMultiplayer check is internal
                });
            }
        });

        this.scene.tweens.add({
            targets: deathText,
            y: y - 80,
            alpha: 0,
            duration: 1500,
            onComplete: () => deathText.destroy()
        });
    }

    public checkWinCondition(isMultiplayer: boolean): void {
        if (this.isGameOver) return;

        // If local player is dead and it's single player, it's Game Over
        if (!isMultiplayer && this.player.isDead()) {
            this.showGameOver(false);
            return;
        }

        // In multiplayer, check if anyone else is alive
        if (isMultiplayer) {
            let aliveRemotePlayers = 0;
            this.remotePlayers.forEach(p => {
                if (p.health > 0) aliveRemotePlayers++;
            });

            // If everyone else is dead and I'm alive, I win
            if (aliveRemotePlayers === 0 && !this.player.isDead()) {
                const stats = this.statsTracker?.finalize([]) ?? [];
                this.showGameOver(true, stats); // Victory!

                // Broadcast victory to everyone (via Host if guest)
                networkManager.broadcast('victory', { winnerId: networkManager.playerId });
            }
            // If I'm dead and everyone else but one is dead, round is over
            else if (this.player.isDead() && aliveRemotePlayers <= 1) {
                // Round is ending soon, show stats after everyone sees victory/defeat
            }
        }
    }

    public showGameOver(isVictory: boolean, stats?: PlayerStats[]): void {
        if (this.isGameOver) return;
        this.isGameOver = true;

        const message = isVictory ? 'VICTORY!' : 'GAME OVER';
        const color = isVictory ? '#f1c40f' : '#e74c3c';

        // Dim background
        const overlay = this.scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
        overlay.setScrollFactor(0).setDepth(200);

        // Result text
        const resultText = this.scene.add.text(GAME_WIDTH / 2, 60, message, {
            fontSize: '64px',
            fontStyle: 'bold',
            color: color,
            stroke: '#000000',
            strokeThickness: 8
        }).setOrigin(0.5).setScrollFactor(0).setDepth(201);

        // Animation for text
        resultText.setScale(0);
        this.scene.tweens.add({
            targets: resultText,
            scale: 1,
            duration: 500,
            ease: 'Back.easeOut'
        });

        if (stats) {
            this.showPostGameStats(stats);
        } else {
            // Simple return to menu for single player or error
            const hintText = this.scene.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 50, 'Returning to menu in 3s...', {
                fontSize: '20px',
                color: '#ffffff'
            }).setOrigin(0.5).setScrollFactor(0).setDepth(201);

            this.scene.time.delayedCall(3000, () => {
                this.scene.cameras.main.fadeOut(1000, 0, 0, 0);
                this.scene.cameras.main.once('camerafadeoutcomplete', () => {
                    this.scene.scene.start('MenuScene');
                });
            });
        }
    }

    public showPostGameStats(stats: PlayerStats[]): void {
        const isMultiplayer = stats.length > 1;

        // Title
        this.scene.add.text(GAME_WIDTH / 2, 120, isMultiplayer ? 'Round Results' : 'Final Results', {
            fontSize: '24px',
            color: '#ffffff',
            fontFamily: 'Arial Black'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(301);

        // Leaderboard rows
        let y = 160;
        const medals = ['🥇', '🥈', '🥉'];

        stats.forEach((s, i) => {
            const medal = medals[i] || `#${s.placement}`;
            const name = s.playerId === networkManager.playerId ? 'YOU' : s.playerId.slice(-4);
            const row = `${medal} ${name} - ${s.damageDealt} dmg, ${s.killCount} kills`;

            this.scene.add.text(GAME_WIDTH / 2, y, row, {
                fontSize: '18px',
                color: i === 0 ? '#f1c40f' : '#ffffff',
                fontFamily: 'Arial'
            }).setOrigin(0.5).setScrollFactor(0).setDepth(301);

            y += 35;
        });

        if (isMultiplayer) {
            this.createMultiplayerButtons(y + 20);
        } else {
            this.createSinglePlayerButtons(y + 20);
        }
    }

    private createSinglePlayerButtons(y: number): void {
        this.createButton(GAME_WIDTH / 2, y, 'Play Again', 0x00b894, () => {
            this.scene.scene.restart();
        });

        this.createButton(GAME_WIDTH / 2, y + 60, 'Menu', 0xd63031, () => {
            this.scene.scene.start('MenuScene');
        });
    }

    private createMultiplayerButtons(y: number): void {
        const readyBtn = this.createButton(GAME_WIDTH / 2, y, 'Ready for Next Round!', 0x6c5ce7, () => {
            readyBtn.destroy();
            waitingText.setVisible(true);
            networkManager.broadcast(SessionMessages.PLAYER_READY, {
                playerId: networkManager.playerId
            });
            // Also notify local scene to check if host can start
            this.scene.events.emit('local-ready');
        });

        const waitingText = this.scene.add.text(GAME_WIDTH / 2, y, 'Waiting for others...', {
            fontSize: '18px',
            color: '#aaaaaa',
            fontStyle: 'italic'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(301).setVisible(false);

        this.createButton(GAME_WIDTH / 2, y + 60, 'Exit to Menu', 0xd63031, () => {
            networkManager.broadcast(SessionMessages.PLAYER_LEFT, {
                playerId: networkManager.playerId
            });
            networkManager.disconnect();
            this.scene.scene.start('MenuScene');
        });
    }

    private createButton(x: number, y: number, text: string, color: number, onClick: () => void): Phaser.GameObjects.Container {
        const bg = this.scene.add.rectangle(0, 0, 240, 40, color)
            .setInteractive({ useHandCursor: true });

        const label = this.scene.add.text(0, 0, text, {
            fontSize: '16px',
            fontFamily: 'Arial',
            fontStyle: 'bold',
            color: '#ffffff'
        }).setOrigin(0.5);

        const container = this.scene.add.container(x, y, [bg, label]);
        container.setScrollFactor(0).setDepth(305);

        bg.on('pointerover', () => bg.setAlpha(0.8));
        bg.on('pointerout', () => bg.setAlpha(1));
        bg.on('pointerup', onClick);

        return container;
    }
}
